import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import TrafficLights from "./TrafficLights";
import WindowFrame from "./WindowFrame";
import useGenie from "../hooks/useGenie";
import { contact } from "../data/search";
import pouchIcon from "../assets/icons/chrome/usepouch.png";
import {
  HOME,
  displayUrl,
  go,
  isReportFrom,
  navigated,
  newTab,
  openTab,
  reloaded,
  reported,
  siteOf,
  stepped,
  urlOf,
} from "../utils/browser";
import gemini from "../assets/icons/chrome/gemini.png";
import ext1 from "../assets/icons/chrome/ext-1.png";
import ext2 from "../assets/icons/chrome/ext-2.png";
import ext3 from "../assets/icons/chrome/ext-3.png";
import ext4 from "../assets/icons/chrome/ext-4.png";
import ext5 from "../assets/icons/chrome/ext-5.png";
import { ReactComponent as BackIcon } from "../assets/svgs/sf-symbols/chrome/arrow.left.svg";
import { ReactComponent as ForwardIcon } from "../assets/svgs/sf-symbols/chrome/arrow.right.svg";
import { ReactComponent as ReloadIcon } from "../assets/svgs/sf-symbols/chrome/arrow.clockwise.svg";
import { ReactComponent as ZoomIcon } from "../assets/svgs/sf-symbols/chrome/magnifyingglass.svg";
import { ReactComponent as StarIcon } from "../assets/svgs/sf-symbols/chrome/star.svg";
import { ReactComponent as PuzzleIcon } from "../assets/svgs/sf-symbols/chrome/puzzlepiece.extension.svg";
import { ReactComponent as PlusIcon } from "../assets/svgs/sf-symbols/chrome/plus.svg";
import { ReactComponent as CloseIcon } from "../assets/svgs/sf-symbols/chrome/xmark.svg";
import { ReactComponent as ChevronDown } from "../assets/svgs/sf-symbols/chrome/chevron.down.svg";
import { ReactComponent as LensIcon } from "../assets/svgs/sf-symbols/chrome/camera.viewfinder.svg";
import { ReactComponent as MoreIcon } from "../assets/svgs/sf-symbols/notes/ellipsis.svg";

const EXTENSIONS = [ext1, ext2, ext3, ext4, ext5];

// Default frame: up to 1180 x 740, a little right of center.
const chromeFrame = (ws) => {
  const w = Math.min(1180, ws.w - 80);
  const h = Math.min(740, ws.h - 32);
  return { x: (ws.w - w) / 2 + 60, y: (ws.h - h) / 2, w, h };
};

// Chrome's "tune" (site settings) icon: two sliders.
const TuneIcon = (props) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" {...props}>
    <path d="M3 6.5h7M15.5 6.5H17M3 13.5h1.5M10 13.5h7" />
    <circle cx="12.8" cy="6.5" r="2.2" />
    <circle cx="7.2" cy="13.5" r="2.2" />
  </svg>
);

// A new tab: a search box and shortcuts to Sylvester's things.
const NewTabPage = ({ navigate }) => {
  const [text, setText] = useState("");
  const shortcuts = [
    { label: "Use Pouch", icon: pouchIcon, run: () => navigate(HOME) },
    { label: "Resume", letter: "R", run: () => window.dispatchEvent(new CustomEvent("open-resume", { detail: {} })) },
    { label: "LinkedIn", letter: "in", run: () => openTab(contact("LinkedIn").href) },
    { label: "Email me", letter: "@", run: () => openTab(contact("Email").href) },
  ];
  return (
    <div className="cr-ntp">
      <form
        className="cr-ntp-search"
        onSubmit={(e) => {
          e.preventDefault();
          go(text, navigate);
        }}
      >
        <ZoomIcon className="cr-ntp-search-icon" aria-hidden="true" />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Search Google or type a URL"
          aria-label="Search Google or type a URL"
        />
      </form>
      <div className="cr-ntp-shortcuts">
        {shortcuts.map((s) => (
          <button type="button" key={s.label} className="cr-ntp-tile" onClick={s.run}>
            <span className="cr-ntp-tile-icon">{s.icon ? <img src={s.icon} alt="" /> : s.letter}</span>
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// Google Chrome, like Sylvester's: tabs in the lavender strip, the toolbar with the address bar,
// his extensions, profile and "Relaunch to update", and pages loaded live. It opens on Use Pouch.
const ChromeWindow = ({ workspace, open, front, onFocus, onClose }) => {
  const [exit, setExit] = useState("closed");
  const genie = useGenie({ app: "Google Chrome", open, exit });
  const [maximized, setMaximized] = useState(false);
  const [tabs, setTabs] = useState(() => [newTab(HOME)]);
  const [activeId, setActiveId] = useState(() => tabs[0].id);
  const [editing, setEditing] = useState(null); // the address bar's text while it's being edited
  const omnibox = useRef(null);
  // Focusing the address bar selects all of it, so typing replaces it. A click places the caret
  // after focusing, so the click's mouse-up selects it all again.
  const [selectAll, setSelectAll] = useState(0);
  const clickedIn = useRef(false);
  useLayoutEffect(() => {
    if (selectAll && omnibox.current) omnibox.current.select();
  }, [selectAll]);
  // Pages only start loading once Chrome has been opened.
  const [started, setStarted] = useState(open);
  useEffect(() => {
    if (open) setStarted(true);
  }, [open]);

  const active = tabs.find((t) => t.id === activeId) || tabs[0];
  const url = urlOf(active);
  const frames = useRef({});

  const update = (id, change) => setTabs((ts) => ts.map((t) => (t.id === id ? change(t) : t)));

  // Loads `to` in the tab, afresh.
  const navigate = (to) => update(active.id, (t) => navigated(t, to));

  const reload = () => update(active.id, reloaded);

  // Back (-1) or Forward (1), worked out (and the page asked) once, outside the state update.
  const step = (delta) => {
    const next = stepped(active, delta, frames.current[active.id]);
    if (next !== active) update(active.id, () => next);
  };

  // Pages that report where they are: follow them in the address bar, title and history.
  useEffect(() => {
    const onMessage = (e) =>
      setTabs((ts) => ts.map((t) => (isReportFrom(e, frames.current[t.id], t.src) ? reported(t, e.data) : t)));
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const hide = (how) => {
    setExit(how);
    if (how === "closed") {
      setMaximized(false);
      // Next time, Chrome opens fresh on Use Pouch.
      const home = newTab(HOME);
      setTabs([home]);
      setActiveId(home.id);
    }
    onClose();
  };

  const addTab = () => {
    const tab = newTab(null);
    setTabs((ts) => [...ts, tab]);
    setActiveId(tab.id);
    setEditing("");
    setTimeout(() => omnibox.current && omnibox.current.focus(), 0);
  };

  const closeTab = (id) => {
    if (tabs.length === 1) {
      hide("closed");
      return;
    }
    const i = tabs.findIndex((t) => t.id === id);
    const rest = tabs.filter((t) => t.id !== id);
    setTabs(rest);
    if (id === activeId) setActiveId(rest[Math.min(i, rest.length - 1)].id);
  };

  const state = open ? "nw--open" : `nw--${exit}`;
  return (
    <WindowFrame
      workspace={workspace}
      initial={chromeFrame}
      min={{ w: 640, h: 400 }}
      open={open}
      front={front}
      zoomed={maximized}
      onFocus={onFocus}
      onToggleZoom={() => setMaximized((m) => !m)}
    >
      <div
        role="dialog"
        aria-label="Google Chrome"
        aria-hidden={!open}
        ref={genie.ref}
        className={`nw cr ${state} ${maximized ? "nw--max" : ""} ${front ? "is-front" : ""} ${
          genie.hidden ? "is-genie-hidden" : ""
        }`}
      >
        <div className="traffic">
          <TrafficLights
            onClose={() => hide("closed")}
            onMinimize={() => genie.minimize(() => hide("minimized"))}
            onPrepare={genie.prepare}
            onZoom={() => setMaximized(!maximized)}
            zoomed={maximized}
          />
        </div>

        <div className="cr-tabstrip no-select">
          <span className="cr-tabsearch" aria-hidden="true">
            <ChevronDown className="cr-sym-chevron" />
          </span>
          <div className="cr-tabs" role="tablist">
            {tabs.map((t) => {
              const tabUrl = urlOf(t);
              const site = tabUrl
                ? { ...siteOf(tabUrl), title: t.title || siteOf(tabUrl).title }
                : { title: "New Tab" };
              return (
                <div
                  key={t.id}
                  role="tab"
                  aria-selected={t.id === active.id}
                  className={`cr-tab ${t.id === active.id ? "is-active" : ""}`}
                  onPointerDown={() => setActiveId(t.id)}
                >
                  {site.icon && <img src={site.icon} alt="" className="cr-favicon" draggable={false} />}
                  <span className="cr-tab-title">{site.title}</span>
                  <button
                    type="button"
                    className="cr-tab-close"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => closeTab(t.id)}
                    aria-label={`Close ${site.title}`}
                  >
                    <CloseIcon />
                  </button>
                </div>
              );
            })}
          </div>
          <button type="button" className="cr-newtab" onClick={addTab} aria-label="New Tab">
            <PlusIcon />
          </button>
          <span className="cr-gemini" aria-hidden="true">
            <img src={gemini} alt="" />
            Ask Gemini
          </span>
        </div>

        <div className="cr-toolbar no-select">
          <button
            type="button"
            className="cr-btn"
            disabled={active.index < 1}
            onClick={() => step(-1)}
            aria-label="Back"
          >
            <BackIcon className="cr-sym-arrow" />
          </button>
          <button
            type="button"
            className="cr-btn"
            disabled={active.index >= active.history.length - 1}
            onClick={() => step(1)}
            aria-label="Forward"
          >
            <ForwardIcon className="cr-sym-arrow" />
          </button>
          <button type="button" className="cr-btn" onClick={reload} aria-label="Reload">
            <ReloadIcon className="cr-sym-reload" />
          </button>

          <form
            className="cr-omnibox"
            onSubmit={(e) => {
              e.preventDefault();
              go(editing || "", navigate);
              setEditing(null);
              omnibox.current.blur();
            }}
          >
            <span className="cr-site-info" aria-hidden="true">
              <TuneIcon className="cr-sym-tune" />
            </span>
            <input
              ref={omnibox}
              value={editing !== null ? editing : url ? displayUrl(url) : ""}
              onPointerDown={() => (clickedIn.current = document.activeElement !== omnibox.current)}
              onFocus={() => {
                setEditing(url || "");
                setSelectAll((n) => n + 1);
              }}
              onMouseUp={(e) => {
                if (!clickedIn.current) return;
                clickedIn.current = false;
                e.preventDefault();
                e.currentTarget.select();
              }}
              onBlur={() => setEditing(null)}
              onChange={(e) => setEditing(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && e.target.blur()}
              placeholder="Search Google or type a URL"
              aria-label="Address and search bar"
              spellCheck={false}
            />
            <span className="cr-omnibox-icons" aria-hidden="true">
              <ZoomIcon className="cr-sym-small" />
              <StarIcon className="cr-sym-small" />
            </span>
          </form>

          <span className="cr-extensions" aria-hidden="true">
            {EXTENSIONS.map((src, i) => (
              <img key={i} src={src} alt="" className="cr-ext" draggable={false} />
            ))}
            <PuzzleIcon className="cr-sym-puzzle" />
          </span>
          <span className="cr-sep" aria-hidden="true" />
          <span className="cr-btn" aria-hidden="true">
            <LensIcon className="cr-sym-small" />
          </span>
          <span className="cr-sep" aria-hidden="true" />
          <span className="cr-avatar" aria-hidden="true">
            S
          </span>
          <span className="cr-relaunch" aria-hidden="true">
            <span className="cr-relaunch-label">Relaunch to update</span>
            <MoreIcon className="cr-sym-more" />
          </span>
        </div>

        <div className="cr-content">
          {started &&
            tabs.map((t) => {
              const shown = t.id === active.id;
              return t.src ? (
                <iframe
                  key={`${t.id}-${t.load}`}
                  ref={(el) => (frames.current[t.id] = el)}
                  src={t.src}
                  title={t.title || siteOf(t.src).title}
                  className="cr-page"
                  hidden={!shown}
                />
              ) : (
                shown && <NewTabPage key={t.id} navigate={navigate} />
              );
            })}
          {/* Clicks inside a page never reach the window, so while another window is in front,
              this catches the first click and brings Chrome forward. */}
          {!front && <div className="cr-shield" onPointerDown={onFocus} />}
        </div>
      </div>
    </WindowFrame>
  );
};

export default ChromeWindow;
